import createClient from 'openapi-fetch'
import type { paths } from './schema'
import i18n from '@/i18n'
import { ApiError, networkError, normalizeApiError } from './errors'
import { reportNetworkFailure, reportNetworkSuccess, reportUnauthenticated } from './events'

const UNSAFE = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])
const CSRF_URL = '/sanctum/csrf-cookie'

export function readXsrfCookie(): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.split('; ').find((c) => c.startsWith('XSRF-TOKEN='))
  if (!match) return null
  try {
    return decodeURIComponent(match.slice('XSRF-TOKEN='.length))
  } catch {
    return null
  }
}

function defaultBaseUrl() {
  // Absolute same-origin URL: valid for Request in browsers and in jsdom/node tests.
  return `${typeof location === 'undefined' ? 'http://localhost' : location.origin}/api/v1`
}

type FetchLike = (input: Request) => Promise<Response>

/**
 * fetch wrapper (used as openapi-fetch's `fetch`): Accept/Accept-Language, CSRF cookie
 * bootstrap, single retry on 419, network failures normalised to ApiError('network_error').
 */
export function createApiFetch(baseFetch: () => (input: Request | string, init?: RequestInit) => Promise<Response>): FetchLike {
  let csrfReady: Promise<void> | null = null

  const ensureCsrf = (force = false) => {
    if (force || !csrfReady) {
      csrfReady = baseFetch()(CSRF_URL, { credentials: 'include', headers: { Accept: 'application/json' } })
        .then((r) => {
          if (!r.ok) throw new Error(`csrf ${r.status}`)
        })
        .catch((e) => {
          csrfReady = null
          throw e
        })
    }
    return csrfReady
  }

  const send = async (request: Request): Promise<Response> => {
    try {
      return await baseFetch()(request)
    } catch (cause) {
      reportNetworkFailure()
      throw networkError(cause)
    }
  }

  return async (original) => {
    const request = original.clone()
    request.headers.set('Accept', 'application/json')
    request.headers.set('Accept-Language', i18n.language || 'en')
    const unsafe = UNSAFE.has(request.method.toUpperCase())

    const prepare = async (req: Request, force: boolean) => {
      if (!unsafe) return req
      try {
        await ensureCsrf(force)
      } catch (cause) {
        reportNetworkFailure()
        throw networkError(cause)
      }
      const token = readXsrfCookie()
      if (token) req.headers.set('X-XSRF-TOKEN', token)
      return req
    }

    const retryCopy = unsafe ? request.clone() : null
    let response = await send(await prepare(request, false))

    if (response.status === 419 && retryCopy) {
      response = await send(await prepare(retryCopy, true))
    }

    reportNetworkSuccess()
    if (response.status === 401) {
      const { pathname } = new URL(request.url, 'http://localhost')
      const isSessionProbe = request.method === 'GET' && pathname.endsWith('/me')
      if (!isSessionProbe && !pathname.endsWith('/auth/login')) reportUnauthenticated()
    }
    return response
  }
}

export function createApiClient(
  baseFetch: () => (input: Request | string, init?: RequestInit) => Promise<Response>,
  baseUrl = defaultBaseUrl(),
) {
  return createClient<paths>({
    baseUrl,
    credentials: 'include',
    fetch: createApiFetch(baseFetch),
  })
}

export const api = createApiClient(() => globalThis.fetch.bind(globalThis))

/**
 * Makes sure the XSRF cookie exists. Used by requests that cannot go through `fetch`
 * (XMLHttpRequest uploads need it too). `force` refreshes it, e.g. after a 419.
 */
export async function ensureCsrfCookie(force = false): Promise<void> {
  if (!force && readXsrfCookie()) return
  try {
    const response = await globalThis.fetch(CSRF_URL, { credentials: 'include', headers: { Accept: 'application/json' } })
    if (!response.ok) throw new Error(`csrf ${response.status}`)
  } catch (cause) {
    reportNetworkFailure()
    throw networkError(cause)
  }
}

interface Result<T> {
  data?: T
  error?: unknown
  response: Response
}

/** Resolves with data or throws a normalized ApiError. 204 resolves to undefined. */
export async function call<T>(promise: Promise<Result<T>>): Promise<T> {
  let result: Result<T>
  try {
    result = await promise
  } catch (e) {
    if (e instanceof ApiError) throw e
    throw networkError(e)
  }
  if (result.response.ok) return result.data as T
  throw normalizeApiError(result.response.status, result.error, result.response.headers)
}
